import { useState } from "react";

import {
  clampScalperMinRun,
  clampScalperWindow,
  DEFAULT_SETTINGS,
  SCALPER_MIN_RUN_MAX,
  SCALPER_MIN_RUN_MIN,
  SCALPER_WINDOW_MAX,
  SCALPER_WINDOW_MIN,
  type UserSettings,
} from "../../lib/settings";

export type DisplaySectionProps = {
  settings: UserSettings;
  onChange: (next: UserSettings) => void;
};

export function DisplaySection({ settings, onChange }: DisplaySectionProps) {
  const [saved, setSaved] = useState(false);

  const update = (patch: Partial<UserSettings["display"]>) => {
    onChange({ ...settings, display: { ...settings.display, ...patch } });
    setSaved(true);
    window.setTimeout(() => {
      setSaved(false);
    }, 1500);
  };

  const rollUpOn = settings.display.rollUpScalperRuns;

  // Drafts let the user type freely; the value is clamped to its valid
  // range (or defaulted on empty/NaN) on blur, then persisted.
  const [windowDraft, setWindowDraft] = useState(
    String(settings.display.scalperWindowMinutes),
  );
  const [minRunDraft, setMinRunDraft] = useState(
    String(settings.display.scalperMinRun),
  );

  const commitWindow = () => {
    const trimmed = windowDraft.trim();
    const clamped =
      trimmed === ""
        ? DEFAULT_SETTINGS.display.scalperWindowMinutes
        : clampScalperWindow(Number(trimmed));
    setWindowDraft(String(clamped));
    update({ scalperWindowMinutes: clamped });
  };

  const commitMinRun = () => {
    const trimmed = minRunDraft.trim();
    const clamped =
      trimmed === ""
        ? DEFAULT_SETTINGS.display.scalperMinRun
        : clampScalperMinRun(Number(trimmed));
    setMinRunDraft(String(clamped));
    update({ scalperMinRun: clamped });
  };

  return (
    <section aria-labelledby="display-heading" className="space-y-6">
      <header>
        <h2
          id="display-heading"
          className="font-display text-xl font-medium tracking-tight text-on-surface"
        >
          Display
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">
          How trades are presented in the app. These preferences are stored on
          this device.
        </p>
      </header>

      <div className="space-y-6">
        <fieldset className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_1fr] sm:items-start sm:gap-4">
          <legend className="float-left pt-1.5 text-sm font-medium text-on-surface sm:float-none">
            Trades grid
          </legend>
          <div className="min-w-0 space-y-4">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                checked={rollUpOn}
                onChange={(event) => {
                  update({ rollUpScalperRuns: event.target.checked });
                }}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                Roll up rapid same-symbol runs
                <span className="mt-0.5 block text-xs text-on-surface-muted">
                  Collapse a burst of same-symbol, same-account trades into one
                  grid row you can expand to its individual trades. Defaults on.
                </span>
              </span>
            </label>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm text-on-surface">
                <span className={rollUpOn ? undefined : "text-on-surface-muted"}>
                  Run window (minutes)
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={SCALPER_WINDOW_MIN}
                  max={SCALPER_WINDOW_MAX}
                  value={windowDraft}
                  disabled={!rollUpOn}
                  aria-label="Run window in minutes"
                  onChange={(event) => {
                    setWindowDraft(event.target.value);
                  }}
                  onBlur={commitWindow}
                  className="w-full rounded-control border border-outline-variant bg-surface px-2 py-1.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-xs text-on-surface-muted">
                  Max gap between trades in a run ({SCALPER_WINDOW_MIN}-
                  {SCALPER_WINDOW_MAX}).
                </span>
              </label>

              <label className="flex flex-col gap-1 text-sm text-on-surface">
                <span className={rollUpOn ? undefined : "text-on-surface-muted"}>
                  Minimum run length
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={SCALPER_MIN_RUN_MIN}
                  max={SCALPER_MIN_RUN_MAX}
                  value={minRunDraft}
                  disabled={!rollUpOn}
                  aria-label="Minimum run length"
                  onChange={(event) => {
                    setMinRunDraft(event.target.value);
                  }}
                  onBlur={commitMinRun}
                  className="w-full rounded-control border border-outline-variant bg-surface px-2 py-1.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-xs text-on-surface-muted">
                  Trades needed to collapse a run ({SCALPER_MIN_RUN_MIN}-
                  {SCALPER_MIN_RUN_MAX}).
                </span>
              </label>
            </div>
          </div>
        </fieldset>

        <div aria-live="polite" className="h-5 text-xs text-on-surface-muted">
          {saved ? "Saved." : ""}
        </div>
      </div>
    </section>
  );
}
